import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Download, 
  FileText, 
  Filter,
  TrendingUp,
  BarChart3,
  Sparkles,
  CheckCircle,
  Copy,
  Check,
  Loader2
} from 'lucide-react';
import { theme } from '../styles/theme';
import { ExportSummary } from '../types';

const ExportContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
`;

const ExportHeader = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
  color: white;
  padding: ${theme.spacing[5]};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1));
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[3]};
    text-align: center;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.md};
  backdrop-filter: blur(10px);
`;

const TitleContent = styled.div`
  h3 {
    margin: 0 0 ${theme.spacing[1]} 0;
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: white;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: ${theme.typography.fontSize.sm};
    color: white;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.md};
  color: white;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const StatsSection = styled.div`
  padding: ${theme.spacing[5]};
  background: ${theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${theme.colors.border};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${theme.spacing[4]};
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  text-align: center;
  transition: all ${theme.transitions.base};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
    border-color: ${theme.colors.primary[500]};
  }
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.primary[600]};
  margin: 0 auto ${theme.spacing[2]} auto;
`;

const StatValue = styled.div<{ $isLoading?: boolean }>`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  
  ${({ $isLoading }) => $isLoading && `
    background: linear-gradient(90deg, 
      ${theme.colors.backgroundSecondary} 25%, 
      ${theme.colors.border} 50%, 
      ${theme.colors.backgroundSecondary} 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: ${theme.borderRadius.sm};
    height: 28px;
    color: transparent;
    
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `}
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const TableSection = styled.div`
  padding: ${theme.spacing[5]};
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[4]};
`;

const TableTitle = styled.h4`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.textPrimary};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSize.sm};
`;

const TableHead = styled.thead`
  background: ${theme.colors.backgroundSecondary};
  
  th {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    text-align: left;
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    border-bottom: 2px solid ${theme.colors.border};
    font-size: ${theme.typography.fontSize.xs};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const TableBody = styled.tbody`
  tr {
    transition: all ${theme.transitions.base};
    
    &:hover {
      background: ${theme.colors.backgroundSecondary};
    }
    
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.border};
    }
  }

  td {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    vertical-align: top;
  }
`;

const QuestionCell = styled.td`
  max-width: 300px;
  
  .question-text {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.medium};
    margin-bottom: ${theme.spacing[1]};
    line-height: ${theme.typography.lineHeight.tight};
  }
  
  .question-meta {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
  }
`;

const MetricCell = styled.td`
  text-align: center;
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ScoreCell = styled.td<{ score: number }>`
  text-align: center;
  
  .score-value {
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing[1]};
    padding: ${theme.spacing[1]} ${theme.spacing[2]};
    border-radius: ${theme.borderRadius.full};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.xs};
    background: ${({ score }) => {
      if (score >= 80) return theme.colors.success[100];
      if (score >= 60) return theme.colors.warning[100];
      return theme.colors.primary[100];
    }};
    color: ${({ score }) => {
      if (score >= 80) return theme.colors.success[700];
      if (score >= 60) return theme.colors.warning[700];
      return theme.colors.primary[700];
    }};
  }
`;

const EnhancementsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  
  li {
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing[1]};
    margin-bottom: ${theme.spacing[1]};
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textSecondary};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const EnhancementIcon = styled.div`
  width: 12px;
  height: 12px;
  background: ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.full};
  margin-top: 2px;
  flex-shrink: 0;
`;

interface ExportSummaryTableProps {
  exportSummary?: ExportSummary;
  isLoading?: boolean;
  /** When true, the run was started in GENERATE mode — drafts were authored
   *  from blank responses, not refined. Swaps "Refinement" → "Generation"
   *  in headings and the per-row improvement column → "Quality score". */
  isGenerateMode?: boolean;
}

const ExportSummaryTable: React.FC<ExportSummaryTableProps> = ({ exportSummary, isLoading = false, isGenerateMode = false }) => {
  const [copied, setCopied] = useState(false);

  if (!exportSummary) {
    return null;
  }

  const verb = isGenerateMode ? 'Generation' : 'Refinement';
  const improvementColLabel = isGenerateMode ? 'Quality' : 'Improvement';
  const enhancementsColLabel = isGenerateMode ? 'Generation Highlights' : 'Key Enhancements';

  const handleCopy = async () => {
    const summaryText = `
Tender Response ${verb}s Report
Generated: ${exportSummary.generated_on}

Summary:
- Total Refinements: ${exportSummary.total_refinements}
- Average Improvement: ${exportSummary.average_improvement === null ? 'pending' : `${exportSummary.average_improvement}%`}
- High Quality Responses: ${exportSummary.high_quality_responses}
- Sections Improved: ${exportSummary.sections_improved.join(', ')}

Detailed Statistics:
${exportSummary.refinement_statistics.map((stat, index) => 
  `${index + 1}. ${stat.question}
   - Improvement Score: ${stat.improvement_score}%
   - Length Change: ${stat.original_length} → ${stat.refined_length} words
   - Key Enhancements: ${stat.key_enhancements.join(', ')}`
).join('\n\n')}
    `.trim();

    await navigator.clipboard.writeText(summaryText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <Sparkles size={12} />;
    if (score >= 60) return <TrendingUp size={12} />;
    return <BarChart3 size={12} />;
  };

  return (
    <ExportContainer>
      <ExportHeader>
        <HeaderContent>
          <TitleSection>
            <HeaderIcon>
              <Download size={24} />
            </HeaderIcon>
            <TitleContent>
              <h3>{verb} Export Summary</h3>
              <p>Generated on {exportSummary.generated_on}</p>
            </TitleContent>
          </TitleSection>
          
          <HeaderActions>
            <ActionButton onClick={handleCopy} title="Copy summary">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </ActionButton>
            <ActionButton title="Download report">
              <Download size={16} />
            </ActionButton>
          </HeaderActions>
        </HeaderContent>
      </ExportHeader>

      <StatsSection>
        <StatsGrid>
          <StatCard>
            <StatIcon>
              <FileText size={16} />
            </StatIcon>
            <StatValue $isLoading={isLoading}>
              {isLoading ? <Loader2 size={16} className="spin" /> : exportSummary.total_refinements}
            </StatValue>
            <StatLabel>Total {isGenerateMode ? 'Drafts' : 'Refinements'}</StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <TrendingUp size={16} />
            </StatIcon>
            <StatValue $isLoading={isLoading}>
              {isLoading ? <Loader2 size={16} className="spin" /> : exportSummary.average_improvement === null ? '—' : `${exportSummary.average_improvement}%`}
            </StatValue>
            <StatLabel>Avg {isGenerateMode ? 'Quality' : 'Improvement'}</StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <Sparkles size={16} />
            </StatIcon>
            <StatValue $isLoading={isLoading}>
              {isLoading ? <Loader2 size={16} className="spin" /> : exportSummary.high_quality_responses}
            </StatValue>
            <StatLabel>High Quality</StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <CheckCircle size={16} />
            </StatIcon>
            <StatValue $isLoading={isLoading}>
              {isLoading ? <Loader2 size={16} className="spin" /> : exportSummary.sections_improved.length}
            </StatValue>
            <StatLabel>{isGenerateMode ? 'Sections Drafted' : 'Sections Enhanced'}</StatLabel>
          </StatCard>
        </StatsGrid>
      </StatsSection>

      <TableSection>
        <TableHeader>
          <TableTitle>
            <BarChart3 size={16} />
            Detailed {verb} Statistics
          </TableTitle>
          <FilterButton>
            <Filter size={14} />
            Filter
          </FilterButton>
        </TableHeader>

        <Table>
          <TableHead>
            <tr>
              <th>Question</th>
              <th>Length Change</th>
              <th>{improvementColLabel}</th>
              <th>{enhancementsColLabel}</th>
            </tr>
          </TableHead>
          <TableBody>
            {exportSummary.refinement_statistics.map((stat, index) => (
              <tr key={index}>
                <QuestionCell>
                  <div className="question-text">
                    {stat.question.length > 60 
                      ? `${stat.question.substring(0, 60)}...` 
                      : stat.question
                    }
                  </div>
                  <div className="question-meta">
                    Question {index + 1}
                  </div>
                </QuestionCell>
                
                <MetricCell>
                  {stat.original_length} → {stat.refined_length} words
                  <br />
                  <span style={{ 
                    color: stat.refined_length > stat.original_length 
                      ? theme.colors.success[600] 
                      : theme.colors.primary[600],
                    fontSize: theme.typography.fontSize.xs
                  }}>
                    {stat.refined_length > stat.original_length ? '+' : ''}
                    {stat.refined_length - stat.original_length} words
                  </span>
                </MetricCell>
                
                <ScoreCell score={typeof stat.improvement_score === 'number' ? stat.improvement_score : 0}>
                  <div className="score-value">
                    {typeof stat.improvement_score === 'number' ? (
                      <>
                        {getScoreIcon(stat.improvement_score)}
                        {stat.improvement_score}%
                      </>
                    ) : (
                      <span style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>—</span>
                    )}
                  </div>
                </ScoreCell>
                
                <td>
                  <EnhancementsList>
                    {stat.key_enhancements.slice(0, 3).map((enhancement, idx) => (
                      <li key={idx}>
                        <EnhancementIcon />
                        {enhancement}
                      </li>
                    ))}
                    {stat.key_enhancements.length > 3 && (
                      <li style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>
                        +{stat.key_enhancements.length - 3} more enhancements
                      </li>
                    )}
                  </EnhancementsList>
                </td>
              </tr>
            ))}
          </TableBody>
        </Table>
      </TableSection>
    </ExportContainer>
  );
};

export default ExportSummaryTable;